/**
 * Synthesizes the Voice Mode thinking chirps. A note (`Syllable`) has a start
 * time, a length, a loudness envelope (attack, release and fade) and a pitch
 * curve. A part is a short run of notes, such as a trill or two chips, and a
 * phrase joins one to three parts, for example chips then a trill.
 *
 * A note's pitch is given as a few `PitchPoint`s. `position` runs from 0 at the
 * note's start to 1 at its end, and `hz` is the pitch at that point. Rendering
 * needs a pitch for every sample, so `createPitchCurve` joins the points with a
 * smooth curve that never overshoots them (a monotone cubic, like SciPy's
 * PCHIP). The sweeps then glide the way a bird's do, without sudden changes of
 * speed at each point.
 *
 * A songbird sings in bouts, like a real bird: one phrase two or three times
 * with small changes, then a different phrase. Each bout also picks how far
 * away the bird is, and whether its longer notes carry a second voice, a tone
 * above the first that real songbirds make with the other side of the syrinx.
 */
type PitchPoint = readonly [position: number, hz: number];

type SecondVoice = { startRatio: number; endRatio: number; level: number };

type Syllable = {
  startSeconds: number;
  durationSeconds: number;
  pitch: readonly PitchPoint[];
  level?: number;
  attackSeconds?: number;
  releaseSeconds?: number;
  fade?: number;
  warble?: { rateHz: number; depthHz: number };
  secondVoice?: SecondVoice;
};

type Part = {
  level: number;
  syllables: (random: () => number) => Syllable[];
};

type Bout = {
  phraseIndex: number;
  syllables: Syllable[];
  singsLeft: number;
  distance: number;
};

const SECOND_HARMONIC = 0.05;
const THIRD_HARMONIC = 0.012;
const PITCH_JITTER = 0.004;
const PITCH_JITTER_RATE_HZ = 40;
const LEVEL_JITTER = 0.06;
const LEVEL_JITTER_RATE_HZ = 60;
const TAIL_SECONDS = 0.02;
const BOUT_PITCH_VARIATION = 0.05;
const BOUT_TEMPO_VARIATION = 0.1;
const SING_PITCH_VARIATION = 0.015;
const SING_TEMPO_VARIATION = 0.04;
const SING_LEVEL_VARIATION = 0.08;
const SINGS_PER_BOUT_MIN = 2;
const SINGS_PER_BOUT_MAX = 3;
const PART_GAP_MIN_SECONDS = 0.08;
const PART_GAP_MAX_SECONDS = 0.16;
const DISTANCES = [0, 0, 0.5, 1];
const FAR_LEVEL_DROP = 0.35;
const FAR_HARMONIC_DROP = 0.7;
const FAR_EXTRA_ATTACK_SECONDS = 0.004;
const NEAR_CUTOFF_HZ = 16_000;
const FAR_CUTOFF_HZ = 6_000;
const TWO_VOICE_BOUT_CHANCE = 0.4;
const TWO_VOICE_NOTE_CHANCE = 0.7;
const TWO_VOICE_MIN_NOTE_SECONDS = 0.05;

function randomInt(random: () => number, min: number, max: number) {
  return min + Math.floor(random() * (max - min + 1));
}

function randomBetween(random: () => number, min: number, max: number) {
  return min + random() * (max - min);
}

function vary(random: () => number, amount: number) {
  return 1 + (random() * 2 - 1) * amount;
}

const TUMBLE: Part = {
  level: 0.466,
  syllables: (random) => {
    const noteCount = randomInt(random, 5, 7);
    const notes: Syllable[] = [];
    let startSeconds = 0;
    let highHz = 4500;
    for (let note = 0; note < noteCount; note += 1) {
      const position = note / (noteCount - 1);
      notes.push({
        startSeconds,
        durationSeconds: 0.055 - 0.02 * position,
        pitch: [
          [0, highHz],
          [0.3, highHz * 0.88],
          [1, highHz * 0.78],
        ],
        level: 1 - 0.4 * position ** 1.5,
        attackSeconds: 0.004,
        releaseSeconds: 0.018,
      });
      startSeconds += 0.15 * 0.82 ** note;
      highHz *= 0.955 + 0.03 * random();
    }
    return notes;
  },
};

const CHEER: Part = {
  level: 0.349,
  syllables: () => [
    {
      startSeconds: 0,
      durationSeconds: 0.24,
      pitch: [
        [0, 3950],
        [0.2, 3700],
        [1, 2500],
      ],
      attackSeconds: 0.012,
      releaseSeconds: 0.05,
      fade: 0.35,
    },
    {
      startSeconds: 0.36,
      durationSeconds: 0.22,
      pitch: [
        [0, 3800],
        [0.2, 3550],
        [1, 2450],
      ],
      level: 0.8,
      attackSeconds: 0.012,
      releaseSeconds: 0.05,
      fade: 0.35,
    },
  ],
};

const TRILL: Part = {
  level: 0.389,
  syllables: (random) => {
    const noteCount = randomInt(random, 6, 10);
    return Array.from({ length: noteCount }, (_, note) => {
      const position = note / (noteCount - 1);
      const highHz = 4300 - 120 * position;
      const lowHz = 3250 - 80 * position;
      const swell = Math.max(0, Math.sin(Math.PI * (0.15 + 0.85 * position)));
      return {
        startSeconds: note * 0.072,
        durationSeconds: 0.042,
        pitch: [
          [0, highHz],
          [0.4, highHz - 0.6 * (highHz - lowHz)],
          [1, lowHz],
        ],
        level: swell ** 0.7 * 0.9 + 0.1,
        attackSeconds: 0.005,
        releaseSeconds: 0.016,
      };
    });
  },
};

const CHIPS: Part = {
  level: 0.467,
  syllables: (random) => {
    const chips: Syllable[] = [
      {
        startSeconds: 0,
        durationSeconds: 0.038,
        pitch: [
          [0, 3300],
          [0.35, 4350],
          [1, 3500],
        ],
        attackSeconds: 0.004,
        releaseSeconds: 0.018,
      },
      {
        startSeconds: 0.24,
        durationSeconds: 0.036,
        pitch: [
          [0, 3200],
          [0.35, 4200],
          [1, 3400],
        ],
        level: 0.75,
        attackSeconds: 0.004,
        releaseSeconds: 0.018,
      },
    ];
    if (random() < 0.5) return chips;
    return [
      ...chips,
      {
        startSeconds: 0.48,
        durationSeconds: 0.034,
        pitch: [
          [0, 3100],
          [0.35, 4050],
          [1, 3300],
        ],
        level: 0.6,
        attackSeconds: 0.004,
        releaseSeconds: 0.018,
      },
    ];
  },
};

const TWITTER: Part = {
  level: 0.368,
  syllables: () => [
    {
      startSeconds: 0,
      durationSeconds: 0.032,
      pitch: [
        [0, 3000],
        [1, 4200],
      ],
      level: 0.7,
      attackSeconds: 0.004,
      releaseSeconds: 0.012,
    },
    {
      startSeconds: 0.065,
      durationSeconds: 0.04,
      pitch: [
        [0, 4600],
        [1, 3600],
      ],
      attackSeconds: 0.004,
      releaseSeconds: 0.015,
    },
    {
      startSeconds: 0.135,
      durationSeconds: 0.06,
      pitch: [
        [0, 3400],
        [0.4, 3850],
        [1, 3300],
      ],
      level: 0.85,
      attackSeconds: 0.006,
      releaseSeconds: 0.02,
    },
    {
      startSeconds: 0.235,
      durationSeconds: 0.09,
      pitch: [
        [0, 3900],
        [1, 3000],
      ],
      level: 0.8,
      attackSeconds: 0.008,
      releaseSeconds: 0.04,
      fade: 0.3,
    },
  ],
};

const CLOSING_CHIP: Part = {
  level: 0.467,
  syllables: () => [
    {
      startSeconds: 0,
      durationSeconds: 0.036,
      pitch: [
        [0, 3150],
        [0.35, 4100],
        [1, 3350],
      ],
      level: 0.7,
      attackSeconds: 0.004,
      releaseSeconds: 0.018,
    },
  ],
};

const PHRASES: Part[][] = [
  ...[TUMBLE, CHEER, TRILL].flatMap((main) => [
    [main],
    [main, CLOSING_CHIP],
    ...[CHIPS, TWITTER].flatMap((intro) => [
      [intro, main],
      [intro, main, CLOSING_CHIP],
    ]),
  ]),
  [CHIPS],
  [TWITTER],
];

function pitchSlopes(points: readonly PitchPoint[]): number[] {
  const widths = points
    .slice(1)
    .map(([position], index) => position - points[index][0]);
  const secants = points
    .slice(1)
    .map(([, hz], index) => (hz - points[index][1]) / widths[index]);
  return points.map((_, index) => {
    if (index === 0) return secants[0];
    if (index === points.length - 1) return secants[index - 1];
    const before = secants[index - 1];
    const after = secants[index];
    if (before * after <= 0) return 0;
    const beforeWeight = 2 * widths[index] + widths[index - 1];
    const afterWeight = widths[index] + 2 * widths[index - 1];
    return (
      (beforeWeight + afterWeight) /
      (beforeWeight / before + afterWeight / after)
    );
  });
}

function createPitchCurve(points: readonly PitchPoint[]) {
  const slopes = pitchSlopes(points);
  return (position: number) => {
    let segment = 0;
    while (segment < points.length - 2 && position > points[segment + 1][0]) {
      segment += 1;
    }
    const [startPosition, startHz] = points[segment];
    const [endPosition, endHz] = points[segment + 1];
    const width = endPosition - startPosition;
    const t = (position - startPosition) / width;
    const t2 = t * t;
    const t3 = t2 * t;
    return (
      (2 * t3 - 3 * t2 + 1) * startHz +
      (t3 - 2 * t2 + t) * width * slopes[segment] +
      (-2 * t3 + 3 * t2) * endHz +
      (t3 - t2) * width * slopes[segment + 1]
    );
  };
}

function createSmoothNoise(
  random: () => number,
  rateHz: number,
  durationSeconds: number
) {
  const knots = Array.from(
    { length: Math.ceil(durationSeconds * rateHz) + 2 },
    () => random() * 2 - 1
  );
  return (seconds: number) => {
    const knot = seconds * rateHz;
    const index = Math.floor(knot);
    const blend = 0.5 - 0.5 * Math.cos(Math.PI * (knot - index));
    return knots[index] + (knots[index + 1] - knots[index]) * blend;
  };
}

function raisedCosine(progress: number) {
  return 0.5 - 0.5 * Math.cos(Math.PI * Math.min(1, Math.max(0, progress)));
}

function renderSyllable(
  output: Float32Array,
  syllable: Syllable,
  sampleRate: number,
  harmonicLevel: number,
  random: () => number
) {
  const {
    startSeconds,
    durationSeconds,
    level = 1,
    attackSeconds = 0.01,
    releaseSeconds = 0.025,
    fade = 0,
    warble,
    secondVoice,
  } = syllable;
  const pitchAt = createPitchCurve(syllable.pitch);
  const pitchJitter = createSmoothNoise(
    random,
    PITCH_JITTER_RATE_HZ,
    durationSeconds
  );
  const levelJitter = createSmoothNoise(
    random,
    LEVEL_JITTER_RATE_HZ,
    durationSeconds
  );
  const firstSample = Math.round(startSeconds * sampleRate);
  const sampleCount = Math.round(durationSeconds * sampleRate);
  let phase = 0;
  let secondPhase = 0;
  for (let sample = 0; sample < sampleCount; sample += 1) {
    const seconds = sample / sampleRate;
    const position = seconds / durationSeconds;
    const warbleHz = warble
      ? warble.depthHz * Math.sin(2 * Math.PI * warble.rateHz * seconds)
      : 0;
    const hz =
      (pitchAt(position) + warbleHz) *
      (1 + PITCH_JITTER * pitchJitter(seconds));
    phase += (2 * Math.PI * hz) / sampleRate;
    let tone =
      Math.sin(phase) +
      harmonicLevel *
        (SECOND_HARMONIC * Math.sin(2 * phase + 0.3) +
          THIRD_HARMONIC * Math.sin(3 * phase + 1.1));
    if (secondVoice) {
      const ratio =
        secondVoice.startRatio +
        (secondVoice.endRatio - secondVoice.startRatio) * position;
      secondPhase += (2 * Math.PI * hz * ratio) / sampleRate;
      tone +=
        secondVoice.level *
        Math.sin(Math.PI * position) *
        Math.sin(secondPhase);
    }
    const envelope =
      raisedCosine(seconds / attackSeconds) *
      raisedCosine((durationSeconds - seconds) / releaseSeconds) *
      (1 - fade * position) *
      (1 + LEVEL_JITTER * levelJitter(seconds));
    output[firstSample + sample] += level * envelope * tone;
  }
}

function softenWithDistance(
  output: Float32Array,
  sampleRate: number,
  distance: number
) {
  const cutoffHz = NEAR_CUTOFF_HZ + (FAR_CUTOFF_HZ - NEAR_CUTOFF_HZ) * distance;
  const smoothing = 1 - Math.exp((-2 * Math.PI * cutoffHz) / sampleRate);
  let previous = 0;
  for (let sample = 0; sample < output.length; sample += 1) {
    previous += smoothing * (output[sample] - previous);
    output[sample] = previous;
  }
}

function addSecondVoice(syllable: Syllable, random: () => number): Syllable {
  const isLongNote = syllable.durationSeconds >= TWO_VOICE_MIN_NOTE_SECONDS;
  if (!isLongNote || random() > TWO_VOICE_NOTE_CHANCE) return syllable;
  const startRatio = randomBetween(random, 1.12, 1.3);
  return {
    ...syllable,
    secondVoice: {
      startRatio,
      endRatio: startRatio + randomBetween(random, -0.1, 0.1),
      level: randomBetween(random, 0.35, 0.5),
    },
  };
}

function endOf(syllables: Syllable[]) {
  return Math.max(
    ...syllables.map(
      ({ startSeconds, durationSeconds }) => startSeconds + durationSeconds
    )
  );
}

function scaleSyllables(
  syllables: Syllable[],
  pitchScale: number,
  tempoScale: number
): Syllable[] {
  return syllables.map((syllable) => ({
    ...syllable,
    startSeconds: syllable.startSeconds * tempoScale,
    durationSeconds: syllable.durationSeconds * tempoScale,
    pitch: syllable.pitch.map(([position, hz]): PitchPoint => [
      position,
      hz * pitchScale,
    ]),
  }));
}

function composePhrase(parts: Part[], random: () => number): Syllable[] {
  const syllables: Syllable[] = [];
  let offsetSeconds = 0;
  for (const part of parts) {
    const partSyllables = part.syllables(random).map((syllable) => ({
      ...syllable,
      startSeconds: offsetSeconds + syllable.startSeconds,
      level: (syllable.level ?? 1) * part.level,
    }));
    syllables.push(...partSyllables);
    offsetSeconds =
      endOf(partSyllables) +
      randomBetween(random, PART_GAP_MIN_SECONDS, PART_GAP_MAX_SECONDS);
  }
  return scaleSyllables(
    syllables,
    vary(random, BOUT_PITCH_VARIATION),
    vary(random, BOUT_TEMPO_VARIATION)
  );
}

function startBout(
  random: () => number,
  previousPhraseIndex: number | null
): Bout {
  const phraseIndexes = PHRASES.map((_, index) => index).filter(
    (index) => index !== previousPhraseIndex
  );
  const phraseIndex =
    phraseIndexes[Math.floor(random() * phraseIndexes.length)];
  const hasSecondVoice = random() < TWO_VOICE_BOUT_CHANCE;
  const syllables = composePhrase(PHRASES[phraseIndex], random).map(
    (syllable) => (hasSecondVoice ? addSecondVoice(syllable, random) : syllable)
  );
  return {
    phraseIndex,
    syllables,
    singsLeft: randomInt(random, SINGS_PER_BOUT_MIN, SINGS_PER_BOUT_MAX),
    distance: DISTANCES[Math.floor(random() * DISTANCES.length)],
  };
}

function renderBout(
  bout: Bout,
  sampleRate: number,
  random: () => number
): Float32Array<ArrayBuffer> {
  const syllables = scaleSyllables(
    bout.syllables,
    vary(random, SING_PITCH_VARIATION),
    vary(random, SING_TEMPO_VARIATION)
  ).map((syllable) => ({
    ...syllable,
    level:
      (syllable.level ?? 1) *
      (1 - FAR_LEVEL_DROP * bout.distance) *
      vary(random, SING_LEVEL_VARIATION),
    attackSeconds:
      (syllable.attackSeconds ?? 0.01) +
      FAR_EXTRA_ATTACK_SECONDS * bout.distance,
  }));
  const output = new Float32Array(
    Math.ceil((endOf(syllables) + TAIL_SECONDS) * sampleRate)
  );
  const harmonicLevel = 1 - FAR_HARMONIC_DROP * bout.distance;
  for (const syllable of syllables) {
    renderSyllable(output, syllable, sampleRate, harmonicLevel, random);
  }
  softenWithDistance(output, sampleRate, bout.distance);
  return output;
}

export type Songbird = { sing(sampleRate: number): Float32Array<ArrayBuffer> };

export function createSongbird(random: () => number = Math.random): Songbird {
  let bout: Bout | null = null;
  return {
    sing: (sampleRate) => {
      if (!bout || bout.singsLeft === 0) {
        bout = startBout(random, bout?.phraseIndex ?? null);
      }
      bout.singsLeft -= 1;
      return renderBout(bout, sampleRate, random);
    },
  };
}
